import { ROW_ERROR_FIELD } from '@/lib/map-field-errors';
import { defaultLibraryGroupForIndex } from '@/pages/Instrument_Runs/components/RunEditForm/assaySelection';

import { createEmptyRows } from './helpers';
import { validateLibraryGroups } from './libraryGroupValidation';
import type { CellErrors, SampleManagerAction, SampleManagerState } from './types';

// Re-export from helpers for backward compatibility
export { createEmptyRows };

/**
 * Reducer for managing SampleManager state
 */
export const sampleManagerReducer = (state: SampleManagerState, action: SampleManagerAction): SampleManagerState => {
  switch (action.type) {
    // Syncs rowData from form state (e.g. draft load).
    case 'SYNC_FROM_FORM':
      return {
        ...state,
        rowData: action.payload.rowData,
      };

    case 'UPDATE_ROW_COUNT':
      return {
        ...state,
        rowData: createEmptyRows(action.payload.count, action.payload.layout),
      };

    case 'UPDATE_ROW': {
      const { rowIndex, patch } = action.payload;
      const updatedRows = state.rowData.map((row, i) => (i === rowIndex ? { ...row, ...patch } : row));

      let nextCellErrors = state.cellErrors;
      const existing = state.cellErrors[rowIndex];
      if (existing) {
        const pruned = { ...existing };
        for (const field of Object.keys(patch)) {
          delete pruned[field];
        }
        // A whole-row backend error (ROW_ERROR_FIELD) carries no concrete column, so it would
        // never be pruned by the patched-field loop above and would stay stuck until the next
        // submit. Clear it on any edit to the row — the user has changed the row's data.
        delete pruned[ROW_ERROR_FIELD];
        nextCellErrors = { ...state.cellErrors };
        if (Object.keys(pruned).length === 0) delete nextCellErrors[rowIndex];
        else nextCellErrors[rowIndex] = pruned;
      }

      return {
        ...state,
        rowData: updatedRows,
        cellErrors: nextCellErrors,
      };
    }

    case 'RESIZE_ROWS': {
      const { count, existingRows, layout } = action.payload;
      const allGeneratedRows = createEmptyRows(count, layout);
      // Overlay existing user data onto the generated rows, preserving entered values
      const mergedRows = allGeneratedRows.map((generatedRow, i) => {
        if (i < existingRows.length) {
          // Preserve user-provided fields (name, study, org, etc.) from existingRows,
          // but always use freshly generated well_position and demux_index so that
          // position metadata is recalculated correctly for the new total count.
          return {
            ...existingRows[i],
            well_position: generatedRow.well_position,
            demux_index: generatedRow.demux_index,
          };
        }
        // New slot — use the generated row with correct position metadata
        return generatedRow;
      });
      return {
        ...state,
        rowData: mergedRows,
        cellErrors: {},
      };
    }

    case 'SET_CELL_ERRORS': {
      const next: CellErrors = {};
      for (const { row, field, msg } of action.payload.triples) {
        if (!next[row]) next[row] = {};
        next[row][field] = msg;
      }
      return { ...state, cellErrors: next };
    }

    case 'CLEAR_CELL_ERRORS':
      return { ...state, cellErrors: {} };

    case 'CLEAR_CELL_ERROR': {
      const { rowIndex, field } = action.payload;
      if (!state.cellErrors[rowIndex]?.[field]) return state;
      const nextRow = { ...state.cellErrors[rowIndex] };
      delete nextRow[field];
      const nextCellErrors = { ...state.cellErrors };
      if (Object.keys(nextRow).length === 0) delete nextCellErrors[rowIndex];
      else nextCellErrors[rowIndex] = nextRow;
      return { ...state, cellErrors: nextCellErrors };
    }

    case 'SET_LIBRARY_GROUPS': {
      const { activeGroups, validate } = action.payload;
      // One active group overwrites every row (so a 2→1 drop, e.g. 24→12 samples, leaves nothing
      // stale); multiple groups only backfill blank rows, keeping values the user already picked.
      const rowData =
        activeGroups.length === 1
          ? state.rowData.map(row => ({ ...row, library_group: activeGroups[0] ?? null }))
          : activeGroups.length > 1
            ? state.rowData.map((row, i) =>
                row.library_group
                  ? row
                  : { ...row, library_group: defaultLibraryGroupForIndex(i, state.rowData.length, activeGroups) },
              )
            : state.rowData;

      // Merge only the library_group key per row — cellErrors is shared with backend/duplicate
      // errors (SET_CELL_ERRORS), which a wholesale replace would silently wipe on every edit.
      let cellErrors = state.cellErrors;
      let warning: string | null = null;
      if (validate) {
        const result = validateLibraryGroups(rowData, activeGroups);
        warning = result.warning;
        cellErrors = { ...state.cellErrors };
        rowData.forEach((_, i) => {
          const libraryGroupMsg = result.cellErrors[i]?.library_group;
          const existingRow = cellErrors[i];
          if (libraryGroupMsg) {
            cellErrors[i] = { ...existingRow, library_group: libraryGroupMsg };
          } else if (existingRow && 'library_group' in existingRow) {
            const { library_group: _drop, ...rest } = existingRow;
            if (Object.keys(rest).length === 0) delete cellErrors[i];
            else cellErrors[i] = rest;
          }
        });
      }

      return { ...state, rowData, cellErrors, libraryGroupWarning: warning };
    }

    default:
      return state;
  }
};
